import { createElement, useEffect, useState } from 'react';

import type { BlockRecord } from '@/lib/blocks/types';
import type { DateFormat, SelectOption } from '@/lib/databases/values';
import type { BlockPropertyRecord, DatabaseColumnDefinition } from '@/lib/databases/types';

import { stopBubbleUnlessFormField } from '@/lib/databases/stop-editor-bubble';

import { DatabaseCell } from './DatabaseCell.web';

type DatabaseRowPanelProps = {
  row: BlockRecord;
  columns: DatabaseColumnDefinition[];
  properties: BlockPropertyRecord[];
  disabled?: boolean;
  onClose: () => void;
  onOpenFullPage?: () => void;
  onSaveTitle: (title: string) => Promise<void>;
  onSaveCell: (
    propertyId: string,
    columnType: string,
    value: Record<string, unknown> | null,
  ) => Promise<void>;
  onEnsureSelectOption?: (columnId: string, name: string) => Promise<SelectOption>;
  onDateFormatChange?: (columnId: string, format: DateFormat) => void;
};

function stopBubble(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

export function DatabaseRowPanel({
  row,
  columns,
  properties,
  disabled,
  onClose,
  onOpenFullPage,
  onSaveTitle,
  onSaveCell,
  onEnsureSelectOption,
  onDateFormatChange,
}: DatabaseRowPanelProps) {
  const [titleDraft, setTitleDraft] = useState(row.title ?? '');
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);

  useEffect(() => {
    setTitleDraft(row.title ?? '');
    setEditingPropertyId(null);
  }, [row.id, row.title]);

  async function commitTitle() {
    const next = titleDraft.trim() || 'Untitled';
    if (next !== (row.title ?? '')) {
      await onSaveTitle(next);
    }
  }

  return createElement(
    'div',
    {
      className: 'cs-db-row-panel-root',
      onMouseDown: stopBubble,
      onPointerDown: stopBubble,
      onClick: stopBubble,
      onKeyDownCapture: stopBubbleUnlessFormField,
    },
    createElement('button', {
      type: 'button',
      className: 'cs-db-row-panel-backdrop',
      'aria-label': 'Close row panel',
      onClick: onClose,
    }),
    createElement(
      'aside',
      { className: 'cs-db-row-panel', role: 'dialog', 'aria-label': 'Row details' },
      createElement(
        'header',
        { className: 'cs-db-row-panel-header' },
        createElement('h3', { className: 'cs-db-row-panel-heading' }, 'Row'),
        createElement(
          'div',
          { className: 'cs-db-row-panel-actions' },
          onOpenFullPage
            ? createElement(
                'button',
                {
                  type: 'button',
                  className: 'cs-db-row-panel-btn',
                  onClick: onOpenFullPage,
                },
                'Open as full page',
              )
            : null,
          createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-row-panel-btn cs-db-row-panel-close',
              onClick: onClose,
              'aria-label': 'Close',
            },
            '×',
          ),
        ),
      ),
      createElement(
        'div',
        { className: 'cs-db-row-panel-body' },
        createElement(
          'div',
          { className: 'cs-db-row-panel-field' },
          createElement('label', { className: 'cs-db-row-panel-label' }, 'Title'),
          createElement('input', {
            type: 'text',
            className: 'cs-db-row-panel-input',
            value: titleDraft,
            disabled,
            onChange: (e: Event) => setTitleDraft((e.target as HTMLInputElement).value),
            onBlur: () => void commitTitle(),
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const el = e.currentTarget as HTMLInputElement;
                void commitTitle().then(() => el.blur());
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setTitleDraft(row.title ?? '');
                (e.currentTarget as HTMLInputElement).blur();
                return;
              }
              e.stopPropagation();
            },
          }),
        ),
        ...columns.map((col) => {
          const prop = properties.find((p) => p.propertyId === col.id);
          return createElement(
            'div',
            { key: col.id, className: 'cs-db-row-panel-field' },
            createElement('label', { className: 'cs-db-row-panel-label' }, col.name),
            createElement(DatabaseCell, {
              column: col,
              property: prop,
              disabled,
              variant: 'panel',
              isEditing: editingPropertyId === col.id,
              onStartEdit: () => setEditingPropertyId(col.id),
              onEndEdit: () => setEditingPropertyId(null),
              onSave: (value) => onSaveCell(col.id, col.type, value),
              onEnsureSelectOption,
              onDateFormatChange,
            }),
          );
        }),
      ),
    ),
  );
}
